/* eslint-disable react/prop-types */
/**
 * UsMonthlyInvestmentDialog — Phase 4 of the US Portfolio.
 *
 * Mirrors the CSE `MonthlyInvestmentDialog` 2-step flow:
 *   Step 1 — user enters a USD amount.
 *   Step 2 — engine returns sector breakdown + per-stock picks.
 *            User ticks the picks they want, optionally edits the
 *            share count, then either:
 *              * Save as pending plan (Option C) — comes back later.
 *              * Execute now — calls the same execute endpoint
 *                immediately after saving.
 *
 * Strict separation: USD throughout; never mixes LKR.
 */
import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, TrendingUp, AlertTriangle, CheckCircle2, ArrowRight,
  Calendar, PlayCircle, Sparkles,
} from "lucide-react";
import { apiPost } from "@/lib/api";
import { toast } from "sonner";

const fmtUsd = (v, opts = {}) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (opts.compact && Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);

const STYLE_LABEL = {
  growth: "Growth",
  balanced: "Balanced",
  income: "Income",
};

const SectorRow = ({ row, status }) => {
  const cfg = {
    over: {
      Icon: AlertTriangle,
      cls: "text-amber-700 dark:text-amber-400",
      label: row.drift_pct > 15 ? "AVOID — significantly overweight" : "AVOID — slightly over",
    },
    ok: {
      Icon: CheckCircle2,
      cls: "text-emerald-700 dark:text-emerald-400",
      label: "On target",
    },
    under: {
      Icon: TrendingUp,
      cls: "text-primary",
      label: row.label || (row.drift_pct < -10 ? "PRIORITISE — most underweight" : "CONSIDER — underweight"),
    },
  }[status];
  const Icon = cfg.Icon;
  return (
    <div
      className="flex items-center gap-2 rounded-md border bg-background/40 px-2 py-1.5"
      data-testid={`us-mi-sector-row-${row.sector.replace(/\s+/g, "-")}-${status}`}
    >
      <Icon className={`h-3.5 w-3.5 ${cfg.cls}`} />
      <span className="font-medium text-sm">{row.sector}</span>
      <span className="text-xs text-muted-foreground">
        Current {row.current_pct?.toFixed(1)}% vs Target {row.target_pct?.toFixed(1)}%
      </span>
      {status === "under" && row.allocation_usd != null && (
        <span className="text-xs text-foreground tabular-nums">
          · {fmtUsd(row.allocation_usd)}
        </span>
      )}
      <Badge variant="outline" className={`ml-auto text-[10px] ${cfg.cls} border-current/30`}>
        {cfg.label}
      </Badge>
    </div>
  );
};

const SIGNAL_CLS = {
  STRONG_BUY: "bg-emerald-600 text-white border-emerald-700",
  BUY: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  WATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
};

const PickRow = ({ pick, checked, onToggle, onEditShares }) => {
  const cost = (pick.suggested_shares || 0) * (pick.current_price || 0);
  return (
    <div
      className={`rounded-md border p-2.5 flex flex-wrap items-center gap-2 transition-colors ${
        checked ? "bg-primary/5 border-primary/30" : "bg-background"
      }`}
      data-testid={`us-mi-pick-${pick.us_symbol}`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        data-testid={`us-mi-pick-toggle-${pick.us_symbol}`}
        aria-label={`Select ${pick.us_symbol}`}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium flex items-center gap-1.5">
          <span className="font-mono text-primary">{pick.us_symbol}</span>
          <span className="text-foreground">· {pick.company_name}</span>
        </div>
        <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-1.5">
          <span>{pick.sector}</span>
          <Badge variant="outline" className={`text-[9px] ${SIGNAL_CLS[pick.composite_signal] || ""}`}>
            {pick.composite_signal} · {pick.composite_score}
          </Badge>
          <span>· {pick.reason}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="0"
          step="1"
          value={pick.suggested_shares}
          onChange={(e) => onEditShares(parseInt(e.target.value || "0", 10) || 0)}
          className="h-8 w-16 text-right tabular-nums"
          data-testid={`us-mi-pick-shares-${pick.us_symbol}`}
        />
        <div className="text-right min-w-[80px]">
          <div className="text-xs text-muted-foreground tabular-nums">@ {fmtUsd(pick.current_price)}</div>
          <div className="text-sm font-semibold tabular-nums">{fmtUsd(cost)}</div>
        </div>
      </div>
    </div>
  );
};

const UsMonthlyInvestmentDialog = ({
  open, onClose, portfolioId, investmentStyle = "balanced",
  onPlanSaved, onExecuted,
}) => {
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState("5000");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  // Pick state — keyed by us_symbol → { selected: bool, shares: int }
  const [pickState, setPickState] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);

  const reset = () => {
    setStep(1);
    setAmount("5000");
    setData(null);
    setPickState({});
    setNotes("");
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  /**
   * Redistribute the user's investment_amount across the *currently
   * selected* picks proportional to the AI's original `allocation_pct`
   * weights. Whenever a checkbox is toggled, the remaining picks
   * proportionally absorb (or release) the budget so the sum always
   * equals the requested USD amount.
   *
   *   E.g. $500 across [JNJ 45%, JPM 35%, GOOGL 20%]
   *   User unticks JNJ ($225). Remaining weights = 35+20 = 55.
   *     JPM   = 500 × 35/55 = $318
   *     GOOGL = 500 × 20/55 = $182
   *     Total = $500 ✅
   *
   * Shares are recomputed as floor(target_usd / current_price). Manual
   * share edits made AFTER a redistribute are preserved until the next
   * toggle event.
   */
  const _redistribute = (picks, prevState, totalAmount) => {
    const next = { ...prevState };
    const selectedPicks = (picks || []).filter(
      (p) => next[p.us_symbol]?.selected,
    );
    const totalWeight = selectedPicks.reduce(
      (acc, p) => acc + (Number(p.allocation_pct) || 0),
      0,
    );
    if (totalWeight <= 0 || !totalAmount) return next;
    selectedPicks.forEach((p) => {
      const targetUsd = ((Number(p.allocation_pct) || 0) / totalWeight) * totalAmount;
      const price = Number(p.current_price) || 0;
      const shares = price > 0 ? Math.max(0, Math.floor(targetUsd / price)) : 0;
      next[p.us_symbol] = { ...next[p.us_symbol], shares };
    });
    return next;
  };

  const analyse = async () => {
    const a = parseFloat(amount);
    if (!a || a <= 0) { toast.error("Enter a valid USD amount"); return; }
    setBusy(true);
    try {
      const res = await apiPost(
        `/us-portfolios/${portfolioId}/monthly-suggest`,
        { amount: a },
      );
      setData(res);
      // Initialise pick state — all picks selected with the AI's suggested
      // share counts. Then immediately re-run redistribute() so the total
      // reflects the FULL investment amount (the AI's per-sector slicing
      // can leave high-price picks at 0 shares; a single redistribute
      // pass absorbs the leftover budget back into the affordable picks).
      const init = {};
      (res.suggestions || []).forEach((p) => {
        init[p.us_symbol] = { selected: true, shares: p.suggested_shares };
      });
      setPickState(_redistribute(res.suggestions || [], init, a));
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate suggestion");
    } finally {
      setBusy(false);
    }
  };

  const togglePick = (sym) => {
    setPickState((prev) => {
      const flipped = {
        ...prev,
        [sym]: { ...prev[sym], selected: !(prev[sym]?.selected) },
      };
      // Re-balance USD across remaining selected picks.
      return _redistribute(data?.suggestions || [], flipped, parseFloat(amount));
    });
  };
  const editShares = (sym, shares) => {
    setPickState((s) => ({
      ...s, [sym]: { ...s[sym], shares },
    }));
  };

  const selectedPicks = useMemo(() => {
    if (!data?.suggestions) return [];
    return data.suggestions
      .filter((p) => pickState[p.us_symbol]?.selected)
      .map((p) => ({
        ...p,
        suggested_shares: pickState[p.us_symbol]?.shares ?? p.suggested_shares,
      }));
  }, [data, pickState]);

  const totalCost = useMemo(
    () => selectedPicks.reduce(
      (acc, p) => acc + (p.current_price || 0) * (p.suggested_shares || 0),
      0,
    ),
    [selectedPicks],
  );

  const buildPlanBody = () => ({
    amount: parseFloat(amount),
    selected_picks: selectedPicks.map((p) => ({
      us_symbol: p.us_symbol,
      company_name: p.company_name,
      sector: p.sector,
      suggested_shares: p.suggested_shares,
      current_price: p.current_price,
      composite_score: p.composite_score,
      composite_signal: p.composite_signal,
      reason: p.reason,
    })),
    notes: notes || null,
  });

  const savePlan = async () => {
    if (selectedPicks.length === 0) { toast.error("Tick at least one pick"); return; }
    setSaving(true);
    try {
      await apiPost(`/us-portfolios/${portfolioId}/monthly-plans`, buildPlanBody());
      toast.success("Pending plan saved", {
        description: `${selectedPicks.length} positions · ${fmtUsd(totalCost)}. ` +
          "You can confirm and execute it from the Health tab when ready.",
      });
      onPlanSaved?.();
      handleClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const executeNow = async () => {
    if (selectedPicks.length === 0) { toast.error("Tick at least one pick"); return; }
    if (!window.confirm(
      `Execute ${selectedPicks.length} positions for ${fmtUsd(totalCost)} now? ` +
      `This will add real holdings to your US portfolio.`
    )) return;
    setExecuting(true);
    try {
      // Save first → execute → done.
      const plan = await apiPost(`/us-portfolios/${portfolioId}/monthly-plans`, buildPlanBody());
      const res = await apiPost(
        `/us-portfolios/${portfolioId}/monthly-plans/${plan.plan_id}/execute`,
        {},
      );
      const ok = (res.executed || []).length;
      const failed = (res.failed || []).length;
      if (failed > 0) {
        toast.warning(`Executed ${ok} positions · ${failed} failed`);
      } else {
        toast.success(`${ok} positions added to your portfolio`);
      }
      onExecuted?.();
      handleClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execute failed");
    } finally {
      setExecuting(false);
    }
  };

  // Reset on each open so a fresh state is presented.
  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="us-monthly-investment-dialog"
      >
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Smart Monthly Investment
                <Badge variant="outline" className="ml-1 text-[10px]">
                  {STYLE_LABEL[investmentStyle?.toLowerCase()] || "Balanced"}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                We'll compare your current sector mix to your{" "}
                <strong>{STYLE_LABEL[investmentStyle?.toLowerCase()] || "Balanced"}</strong>{" "}
                target and suggest US BUY-rated stocks from{" "}
                <strong>underweight sectors only</strong>. All amounts in USD.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="us-mi-amount">New funds to invest (USD)</Label>
                <Input
                  id="us-mi-amount"
                  type="number"
                  min="1"
                  step="100"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="us-mi-amount-input"
                />
                <p className="text-[11px] text-muted-foreground">
                  Typical monthly DCA range: $500 – $10,000.
                </p>
              </div>
              <div className="rounded-md border border-dashed border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground space-y-1.5">
                <div className="font-medium text-foreground flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  How does this work?
                </div>
                <p>
                  Sectors more than 1pp below target are flagged{" "}
                  <span className="text-foreground font-medium">underweight</span>.
                  Your USD amount is split proportional to drift magnitude
                  (deepest underweight gets the biggest slice). For each sector
                  we pick the top 1–2 BUY/STRONG_BUY US stocks scored ≥60.
                </p>
                <p>
                  No tax or brokerage fees are applied — that's broker-specific.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={analyse} disabled={busy} data-testid="us-mi-analyse-btn">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}
                Analyse & suggest <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Suggested for {fmtUsd(parseFloat(amount))}
              </DialogTitle>
              <DialogDescription>{data?.message}</DialogDescription>
            </DialogHeader>

            {/* Sector rows */}
            {data && (
              <div className="space-y-1.5 text-xs">
                {(data.overweight || []).slice(0, 3).map((r) => (
                  <SectorRow key={`over-${r.sector}`} row={r} status="over" />
                ))}
                {(data.on_target || []).slice(0, 2).map((r) => (
                  <SectorRow key={`ok-${r.sector}`} row={r} status="ok" />
                ))}
                {(data.underweight || []).map((r) => (
                  <SectorRow key={`under-${r.sector}`} row={r} status="under" />
                ))}
              </div>
            )}

            {/* Pick list */}
            {(data?.suggestions || []).length > 0 ? (
              <div className="space-y-2 mt-2">
                <div className="text-xs text-muted-foreground">
                  Tick the picks you want and adjust share counts as needed. Unticking a pick
                  proportionally redistributes its budget to the remaining selections so the
                  total always matches your investment amount.
                </div>
                {data.suggestions.map((p) => (
                  <PickRow
                    key={p.us_symbol}
                    pick={{ ...p, suggested_shares: pickState[p.us_symbol]?.shares ?? p.suggested_shares }}
                    checked={!!pickState[p.us_symbol]?.selected}
                    onToggle={() => togglePick(p.us_symbol)}
                    onEditShares={(sh) => editShares(p.us_symbol, sh)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-muted-foreground">
                {data?.message || "No suggestions available."}
              </div>
            )}

            {/* Running total strip — sticky-feeling summary */}
            {(data?.suggestions || []).length > 0 && (() => {
              const target = parseFloat(amount) || 0;
              const diff = totalCost - target;
              const within = Math.abs(diff) <= Math.max(target * 0.01, 5);
              const showRoundingNote = !within && Math.abs(diff) > 10 && selectedPicks.length > 0;
              const stateCls = selectedPicks.length === 0
                ? "border-muted bg-muted/20 text-muted-foreground"
                : within
                  ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
                  : "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400";
              return (
                <>
                  <div
                    className={`rounded-md border p-2.5 flex items-center justify-between text-sm ${stateCls}`}
                    data-testid="us-mi-running-total"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Selected:</span>
                      <span className="tabular-nums">{selectedPicks.length} positions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="tabular-nums font-semibold" data-testid="us-mi-running-total-value">
                        {fmtUsd(totalCost)}
                      </span>
                      <span className="text-muted-foreground">/</span>
                      <span className="tabular-nums">{fmtUsd(target)}</span>
                      <span aria-hidden>
                        {selectedPicks.length === 0 ? "—" : within ? "✅" : "⚠"}
                      </span>
                    </div>
                  </div>
                  {showRoundingNote && (
                    <div
                      className="rounded-md border border-muted-foreground/20 bg-muted/30 p-2.5 text-[12px] text-muted-foreground space-y-1"
                      data-testid="us-mi-rounding-note"
                    >
                      <div className="flex items-baseline gap-2 text-foreground">
                        <span className="text-base leading-none">ℹ️</span>
                        <span>
                          Difference: <span className="font-semibold tabular-nums">{fmtUsd(Math.abs(diff))}</span>{" "}
                          ({diff > 0 ? "over" : "under"} target).
                        </span>
                      </div>
                      <div className="leading-snug pl-6">
                        This is normal — stocks are purchased in <strong className="text-foreground">whole shares</strong>.
                        The remaining cash stays in your brokerage account and can be deployed in your next
                        monthly run.
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Notes */}
            {(data?.suggestions || []).length > 0 && (
              <div className="space-y-1.5 mt-2">
                <Label htmlFor="us-mi-notes" className="text-xs">Plan notes (optional)</Label>
                <Input
                  id="us-mi-notes"
                  placeholder="e.g. May 2026 DCA"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="us-mi-notes-input"
                />
              </div>
            )}

            <DialogFooter className="flex-wrap gap-2">
              <Button
                variant="ghost"
                onClick={() => { setStep(1); setData(null); }}
                disabled={busy || saving || executing}
              >
                ← Back
              </Button>
              <Button variant="outline" onClick={handleClose} disabled={saving || executing}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={savePlan}
                disabled={saving || executing || selectedPicks.length === 0}
                data-testid="us-mi-save-plan-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Calendar className="h-3.5 w-3.5 mr-1.5" />}
                Save as pending plan
              </Button>
              <Button
                onClick={executeNow}
                disabled={saving || executing || selectedPicks.length === 0}
                data-testid="us-mi-execute-btn"
              >
                {executing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
                Execute now
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UsMonthlyInvestmentDialog;
